import { useAtomValue } from "jotai";
import { KeyValuePair } from "../../types";
import { useFilterHelper, useInstruments } from "../../hooks";
import { AccountAtom, TransactionsAtom, ViewModeAtom } from "../../store";
import { useEffect, useMemo, useState } from "react";
import { CashFlowEx } from "../../types/Cashflow";
import { getCashFlows } from "../../api/Cashflow";
import { determineDateRange } from "../../components";
import { track, EVENTS } from "../../utils/analytics";

interface UseCashFlowController {
  rows: CashFlowEx[];
  selectedRows: CashFlowEx[];
  setSelectedRows: (trades: CashFlowEx[]) => void;
  loading: boolean;
  showUnCategorised: boolean;
  setShowUnCategorised: (value: boolean) => void;
  showUnBooked: boolean;
  setShowUnBooked: (value: boolean) => void;
  showNoPositionGrouping: boolean;
  setShowNoPositionGrouping: (value: boolean) => void;
  showHidden: boolean;
  setShowHidden: (value: boolean) => void;
  setSelectedDateRange: (item: KeyValuePair) => void;
  refresh: () => void;
}

export const useCashflowsController = (): UseCashFlowController => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<CashFlowEx[]>([]);
  const [selectedRows, setSelectedRows] = useState<CashFlowEx[]>([]);
  const [showUnCategorised, setShowUnCategorised] = useState(false);
  const [showUnBooked, setShowUnBooked] = useState(false);
  const [showNoPositionGrouping, setShowNoPositionGrouping] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const instruments = useInstruments();
  const accountState = useAtomValue(AccountAtom);
  const filterHelper = useFilterHelper();
  const viewMode = useAtomValue(ViewModeAtom);

  const [selectedDateRange, setSelectedDateRange] = useState<KeyValuePair>();

  // Wrap setters with analytics tracking
  const setSelectedRowsWithTracking = (rows: CashFlowEx[]) => {
    setSelectedRows(rows);
    if (rows.length > 0) {
      track(EVENTS.CASHFLOW.ROW_SELECTED, {
        count: rows.length,
        descriptions: rows.map((row) => row.description || "unknown"),
      });
    }
  };

  const setShowUnCategorisedWithTracking = (value: boolean) => {
    setShowUnCategorised(value);
    track(EVENTS.CASHFLOW.TOGGLE_UNCATEGORISED, { value });
  };

  const setShowUnBookedWithTracking = (value: boolean) => {
    setShowUnBooked(value);
    track(EVENTS.CASHFLOW.TOGGLE_UNBOOKED, { value });
  };

  const setShowNoPositionGroupingWithTracking = (value: boolean) => {
    setShowNoPositionGrouping(value);
    track(EVENTS.CASHFLOW.TOGGLE_NO_POSITION_GROUPING, { value });
  };

  const setShowHiddenWithTracking = (value: boolean) => {
    setShowHidden(value);
    // track(EVENTS.CASHFLOW.TOGGLE_HIDDEN, { value });
  };

  const setSelectedDateRangeWithTracking = (item: KeyValuePair) => {
    setSelectedDateRange(item);
    track(EVENTS.CASHFLOW.DATE_RANGE_CHANGED, {
      range: item.key,
      label: item.value,
    });
  };

  const filteredRows = useMemo(() => {
    let result = filterHelper.filteredUnderlying
      ? rows.filter((row) => {
          const underlyingInstrument = instruments.list.find((x) => {
            return (
              x.id === row.underlyingInstrumentId || x.id === row.instrumentId
            );
          });

          if (
            underlyingInstrument?.name === filterHelper.filteredUnderlying ||
            underlyingInstrument?.symbol === filterHelper.filteredUnderlying ||
            underlyingInstrument?.symbol.split("_")[0] ===
              filterHelper.filteredUnderlying
          ) {
            return true;
          } else {
            return false;
          }
        })
      : rows;

    if (filterHelper.filteredTags && filterHelper.filteredTags !== "All") {
      result = result.filter((row) => {
        return row?.tags?.includes(filterHelper.filteredTags!);
      });
    }

    if (showUnCategorised) {
      result = result.filter((row) => !row.category);
    }

    if (showUnBooked) {
      result = result.filter((row) => !row.book);
    }

    if (showNoPositionGrouping) {
      result = result.filter(
        (row) =>
          !row.positionGroup || row.positionGroup.toLowerCase() === "ungrouped"
      );
    }

    if (
      filterHelper.filteredPositionGroup &&
      filterHelper.filteredPositionGroup !== "All"
    ) {
      result = result.filter(
        (row) =>
          row.positionGroup?.toLowerCase() ===
          filterHelper.filteredPositionGroup!.toLowerCase()
      );
    }

    if (
      filterHelper.filteredStrategy &&
      filterHelper.filteredStrategy !== "All"
    ) {
      result = result.filter(
        (row) => row.strategy === filterHelper.filteredStrategy!
      );
    }

    if (filterHelper.filteredBook && filterHelper.filteredBook !== "All") {
      result = result.filter(
        (row) =>
          row.book?.toLowerCase() === filterHelper.filteredBook!.toLowerCase()
      );
    }

    return result.map((row) => {
      return {
        ...row,
        instrument: row.instrumentId
          ? instruments.list.find((x) => x.id === row.instrumentId)
          : undefined,
        underlyingInstrument: row.underlyingInstrumentId
          ? instruments.list.find((x) => x.id === row.underlyingInstrumentId)
          : instruments.list.find((x) => x.id === row.instrumentId),
      } as CashFlowEx;
    });
  }, [
    filterHelper,
    rows,
    showNoPositionGrouping,
    showUnBooked,
    showUnCategorised,
  ]);

  const refresh = () => {
    setSelectedRows([]);
    track(EVENTS.CASHFLOW.CASHFLOW_EXPLAINED);
    loadData(true);
  };

  const loadData = (force: boolean = false) => {
    if (
      selectedDateRange?.key &&
      accountState.selectedAccount?.id &&
      (force || !loading)
    ) {
      setLoading(true);
      setTimeout(async () => {
        try {
          const dates = determineDateRange(selectedDateRange?.key);
          const result = await getCashFlows(
            accountState.selectedAccount.id,
            dates.start,
            dates.end,
            showHidden
          );
          setRows(result?.data);
        } finally {
          setLoading(false);
        }
      }, 1);
    }
  };

  const transactions = useAtomValue(TransactionsAtom);

  useEffect(() => {
    loadData();
  }, [accountState.selectedAccount, selectedDateRange, transactions.cashflows, showHidden]);

  // Refetch cashflows when switching between simple and advanced mode
  useEffect(() => {
    if (selectedDateRange?.key && accountState.selectedAccount?.id) {
      loadData(true);
    }
  }, [viewMode]);

  return {
    rows: filteredRows,
    loading,
    showUnCategorised,
    setShowUnCategorised: setShowUnCategorisedWithTracking,
    showUnBooked,
    setShowUnBooked: setShowUnBookedWithTracking,
    showNoPositionGrouping,
    setShowNoPositionGrouping: setShowNoPositionGroupingWithTracking,
    showHidden,
    setShowHidden: setShowHiddenWithTracking,
    setSelectedDateRange: setSelectedDateRangeWithTracking,
    selectedRows,
    setSelectedRows: setSelectedRowsWithTracking,
    refresh,
  };
};
